import type { BaseMasterRecord } from '@/features/masters';
import type {
  HierarchyScope,
  HierarchyScopeView,
  TemplateDetail,
  TemplateFormFieldValue,
  TemplateFormSubmissionPayload,
  TemplateFormValue,
} from '@/features/templates';

/**
 * The Purchase Request (submitted form) wire types, mirroring the backend's `/purchase-requests` contract. The
 * Default Template Forms screen and its `/template-forms` API were removed on 2026-10-16.
 */

/**
 * One row of `GET /purchase-requests` -- a submitted form.
 *
 * A `BaseMasterRecord`, because the list is drawn by the masters' own list
 * table: `id`, the audit quartet and `createdByName` are what that table reads.
 */
export interface TemplateFormSubmissionRow extends BaseMasterRecord {
  /** `PR-000123`. */
  referenceNo: string;
  templateId: number;
  /** The template's name when the form was submitted (a snapshot). */
  templateName: string;
  /** The template's kind at submit time: any kind. */
  templateKind: string;
  /** How many fields were answered. */
  valueCount: number;
  /** The division it was submitted for, which chose its workflow. Null before divisions existed. */
  divisionId?: number | null;
  divisionName?: string | null;
  workflowState: SubmissionWorkflowState;
  /** The current status: the pending step's configured label, or the outcome. */
  workflowStatus: string;
  currentStep?: number | null;
  /**
   * The signed-in user may decide this form now: its current step awaits them and they hold UPDATE on
   * Purchase Requests. Computed by the server, which also decides which rows are listed at all.
   */
  canDecide?: boolean;
  /** The Departments it was submitted for, by name, comma-separated; absent where it named none. */
  departmentNames?: string | null;
  /* A Purchase Request's list columns (NB-355); absent on other forms. */

  /** The request's PR Title answer (2026-10-09); absent where the template has none. */
  title?: string | null;
  /** The Delivery Date, `yyyy-MM-dd`. */
  requiredBy?: string | null;
  itemCount?: number | null;
  processingStatus?: PurchaseRequestProcessingStatus | null;
  processingStatusLabel?: string | null;
  /** The PR Template's code -- the list's Template ID (design16, 2026-10-19); absent where it no longer exists. */
  templateCode?: string | null;
  /** The request's Priority (design16): Low, Medium, High or Urgent. */
  priority?: PurchaseRequestPriority | null;
}

/** A Purchase Request's Priority (design16, 2026-10-19), Medium unless chosen. */
export type PurchaseRequestPriority = 'Low' | 'Medium' | 'High' | 'Urgent';

/** The form's Priority choices, in the design's order, and the one a new request starts on. */
export const PURCHASE_REQUEST_PRIORITIES: readonly PurchaseRequestPriority[] = ['Low', 'Medium', 'High', 'Urgent'];
export const DEFAULT_PURCHASE_REQUEST_PRIORITY: PurchaseRequestPriority = 'Medium';


/** How far a Purchase Request has been converted to RFQs -- the reference's Processing Status. */
export type PurchaseRequestProcessingStatus = 'UNPROCESSED' | 'PARTIAL_PROCESSED' | 'PROCESSED' | 'DRAFT';

/**
 * The reference's status dropdown, as the server's `processingStatus` parameter reads it. '' is every status. Worded as
 * design16 words them -- "Partial Processed" (2026-10-19); the server's label stays "Partially Processed".
 */
export const PROCESSING_STATUS_FILTERS: ReadonlyArray<{ value: '' | PurchaseRequestProcessingStatus; label: string }> = [
  { value: '', label: 'All Status' },
  { value: 'UNPROCESSED', label: 'Unprocessed' },
  { value: 'PARTIAL_PROCESSED', label: 'Partially Processed' },
  { value: 'PROCESSED', label: 'Processed' },
  { value: 'DRAFT', label: 'Draft' },
];

/** One status card: how many stand at it now, and the last 30 days against the 30 before, whole percent. */
export interface PurchaseRequestSummaryCard {
  count: number;
  changePercent: number;
}

/** `GET /purchase-requests/summary` -- the reference's cards, and Draft. */
export interface PurchaseRequestSummary {
  total: PurchaseRequestSummaryCard;
  unprocessed: PurchaseRequestSummaryCard;
  processed: PurchaseRequestSummaryCard;
  partialProcessed: PurchaseRequestSummaryCard;
  draft: PurchaseRequestSummaryCard;
}


/**
 * The engine's own state (`SubmissionWorkflowState`). DRAFT: a Purchase Request saved with Status Inactive on create
 * (2026-10-09) -- numbered, no workflow started, submitted once an edit sets it Active.
 */
export type SubmissionWorkflowState = 'NO_WORKFLOW' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'SENT_BACK' | 'DRAFT';

/** Whether a request is a draft: saved, not yet submitted. */
export function isDraftState(state: SubmissionWorkflowState | null | undefined): boolean {
  return state === 'DRAFT';
}

/** What an approver may decide. */
export type SubmissionDecision = 'APPROVE' | 'REJECT' | 'SEND_BACK';

/** One step of a submission's workflow. */
export interface SubmissionStep {
  sequence: number;
  stepName: string;
  approverType: string;
  /** Who it routes to, for display: role names or a person. */
  approverLabel?: string | null;
  state: 'WAITING' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'SENT_BACK';
  pendingStatus?: string | null;
  approvedStatus?: string | null;
  actedBy?: number | null;
  actedByName?: string | null;
  actedDate?: string | null;
  comment?: string | null;
}

/** One entry of a submission's history. */
export interface SubmissionHistoryEntry {
  id: number;
  stepSequence?: number | null;
  stepName?: string | null;
  /** UPDATED, DEACTIVATED and REACTIVATED: a Purchase Request edited, deleted (marked Inactive) and put back to
   * Active from its edit form, after it was submitted. DRAFTED: saved as a draft (Inactive on create). */
  action:
    | 'SUBMITTED'
    | 'APPROVED'
    | 'REJECTED'
    | 'SENT_BACK'
    | 'COMPLETED'
    | 'UPDATED'
    | 'DEACTIVATED'
    | 'REACTIVATED'
    | 'DRAFTED';
  /** The status before this action; absent for the submission itself and for older history. */
  previousStatus?: string | null;
  resultStatus: string;
  comment?: string | null;
  /** What the person acting was told: the step's configured message, or the standard sentence. */
  message?: string | null;
  actedBy?: number | null;
  actedByName?: string | null;
  actedDate?: string | null;
}

/**
 * The submit body: the template's payload plus the organizational context it is submitted for -- Entities,
 * Business Units, Divisions and Departments, at least one Division. The workflow runs in `divisionId` when one
 * is named (it must be one of the scope's), otherwise in the scope's first Division.
 */
export interface TemplateFormSubmitRequest extends TemplateFormSubmissionPayload {
  scope: HierarchyScope;
  divisionId?: number | null;
  /** A Purchase Request's lines (NB-355). */
  lineItems?: PurchaseRequestLine[];
  /** A Purchase Request's number, as `GET /purchase-requests/next-number` reserved it on load. */
  referenceNo?: string;
  /**
   * The record's own Status, the form's Active/Inactive switch (design21); the server accepts only those two names.
   * A body naming no `draft` keeps the older rule: on a create Inactive saves a draft, on a draft's edit Active
   * submits it (2026-10-09).
   */
  statusName?: 'Active' | 'Inactive';
  /**
   * Whether a Purchase Request is saved as a draft (design21, 2026-10-22): true for Save as Draft, false for Submit
   * Request and Update Request -- its Status is then the switch's, whichever way it is set.
   */
  draft?: boolean;
  /** A Purchase Request's Priority (design16); left out, Medium on a new request and unchanged on an edit. */
  priority?: PurchaseRequestPriority;
}

/** One line of a Purchase Request as sent: the three masters' ids and a quantity. */
export interface PurchaseRequestLine {
  locationId: number;
  categoryId: number;
  productId: number;
  quantity: number;
  /** The line's answers to the fields added to the PR Template's Line items step. */
  values?: TemplateFormFieldValue[];
  /**
   * When the line's product is needed, `yyyy-MM-dd`, or null (2026-10-23, from the base team's PR master's Add Product
   * popup). Optional at the server: a line saved before it existed has none.
   */
  requiredBy?: string | null;
  /** The line's free-text remarks, at most {@link PR_LINE_REMARKS_MAX} characters, or null. */
  remarks?: string | null;
}

/** The longest a line's Remarks may be -- the server holds the same limit. */
export const PR_LINE_REMARKS_MAX = 500;

/**
 * `GET /purchase-requests/{id}/usage`: how many records refer to the request -- BIDs raised against it, and Purchase
 * Orders (always 0 until Purchase Orders exist).
 */
export interface PurchaseRequestUsage {
  bids: number;
  purchaseOrders: number;
}

/** One line of a submitted Purchase Request, with the labels its masters had then. */
export interface PurchaseRequestLineView {
  lineNo: number;
  locationId: number;
  locationLabel?: string | null;
  categoryId: number;
  categoryLabel?: string | null;
  productId: number;
  productLabel?: string | null;
  quantity: number;
  /** How much of it has been converted to RFQs. */
  convertedQuantity?: number;
  /** Its answers to the fields added to the Line items step, typed as submitted. */
  values?: SubmittedValue[];
  /** When it is needed, `yyyy-MM-dd`; null or absent on a line saved without one. */
  requiredBy?: string | null;
  /** Its remarks; null or absent where it has none. */
  remarks?: string | null;
}

/** A line-item dropdown's option -- a location, category or product -- labelled `CODE - Name`. */
export interface CatalogOption {
  id: number;
  code?: string | null;
  name: string;
  label: string;
  /** A product's UoM name, shown after its line's Quantity; absent on locations and categories. */
  uomName?: string | null;
  /** A product's Purchase Group and Cost Center Code, which its line fills in (2026-10-22); absent on the others. */
  purchaseGroup?: string | null;
  costCenterCode?: string | null;
}

/** Which master a line-item dropdown reads. */
export type CatalogKind = 'locations' | 'categories' | 'products';

/** One answered field of a submitted form, typed as it was submitted. */
export interface SubmittedValue {
  sectionKey: string;
  fieldKey: string;
  /** Snapshots: the field as it was when the form was submitted. */
  fieldLabel: string;
  fieldType: string;
  ordinal: number;
  value: TemplateFormValue;
}

/** `GET /purchase-requests/{id}`: one submitted form with every answer. */
export interface TemplateFormSubmissionDetail extends TemplateFormSubmissionRow {
  /** The configuration it was filled in from -- the template's version reference. */
  templateModifiedDate: string;
  values: SubmittedValue[];
  steps: SubmissionStep[];
  history: SubmissionHistoryEntry[];
  /** What the signed-in user may decide now; empty unless they are the pending step's approver. */
  availableDecisions: SubmissionDecision[];
  /** The pending step's configured approve-button label. */
  approveLabel?: string | null;
  /** The organizational context it was submitted for, labelled. Absent on forms submitted before it existed. */
  scope?: HierarchyScopeView | null;
  /** A Purchase Request's lines, as submitted; empty on any other form. */
  lineItems?: PurchaseRequestLineView[];
  /**
   * The template as it stood when the form was created -- the layout View and Edit draw it in, so a later change
   * to the template never reaches it. Null on a form created before copies were kept; that one follows the live
   * template.
   */
  template?: TemplateDetail | null;
}


/**
 * The pill tone a workflow state reads as, from the masters' two tones: a
 * finished-well form is green, everything else neutral. The status TEXT always
 * says which it is, so the colour is never the only signal.
 */
export function workflowTone(state: SubmissionWorkflowState): 'active' | 'inactive' {
  return state === 'COMPLETED' || state === 'NO_WORKFLOW' ? 'active' : 'inactive';
}

/** What a decision answers: the updated form, and the message the approver is shown. */
export interface SubmissionDecisionResult {
  submission: TemplateFormSubmissionDetail;
  /** The step's configured Approve / Reject Message, or the standard sentence. */
  message: string;
}

/** The sentence shown when the platform sends none -- the platform's own standard wording. */
export function standardDecisionMessage(submission: TemplateFormSubmissionDetail): string {
  return `Recorded. ${submission.referenceNo} is now ${submission.workflowStatus}.`;
}

/** What `POST /purchase-requests` answers with, on a 201. */
export interface TemplateFormSubmissionResult {
  id: number;
  /** What a person quotes: `PR-000123`. */
  referenceNo: string;
  templateId: number;
  templateName: string;
  templateKind: string;
  statusId?: number | null;
  statusName?: string | null;
  createdBy?: number | null;
  createdDate?: string | null;
  divisionId?: number | null;
  workflowState?: SubmissionWorkflowState;
  workflowStatus?: string;
}

/**
 * The per-field messages on a refused submission, keyed `sectionKey.fieldKey` -- the shared reader, moved to
 * `core/error-handling` so the template builder reads a refused save the same way (2026-10-09).
 */
export { fieldErrorsOf } from '@/core/error-handling/ApiErrorHandler';

/** Whether a listing query asks for My Approvals (`awaitingMe`, carried as 1 through the list engine). */
export function isAwaitingMe(query: object): boolean {
  const flag = (query as { awaitingMe?: unknown }).awaitingMe;
  return flag === true || flag === 1 || flag === '1' || flag === 'true';
}
